import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {ErrorMessage } from '../../common';
import LoadingSpinner from '../../ui/LoadingSpinner';
import { setPickupGroupDetails, setPickupDetails } from '../../../redux/slices/checkoutSlice';
import { updateCartItemDeliveryDetails } from '../../../redux/slices/cartSlice';
import { formatPickupLocation, getAvailableDates } from './utils/pickupUtils';
import ItemThumbnails from './components/ItemThumbnails';
import PickupLocationList from './components/PickupLocationList';
import { usePickupData } from '../../../hooks/usePickupData';

const PickupMethodSection = ({ 
  locationId = 'default',
  title = "Select Pickup Location & Time", 
  items: itemsFromProps,
  onComplete, 
  setError,
}) => {
  const dispatch = useDispatch();
  const { buyProducts, rentProducts } = useSelector(state => state.cart);

  const {
    pickupItems,
    enhancedLocations,
    locationDistances,
    isLoading: pickupDataLoading,
    error: pickupDataError,
    isLocationFailed,
    firstAddressIdForEffect
  } = usePickupData(itemsFromProps, buyProducts, rentProducts);

  const [selectedPickupGroupId, setSelectedPickupGroupId] = useState(null);
  const [internalError, setInternalError] = useState(null);

  useEffect(() => {
    if (pickupDataError && setError) {
      setError('Failed to load pickup location details. Some locations may be unavailable.');
      if (selectedPickupGroupId && enhancedLocations[selectedPickupGroupId]) {
        const selectedLocation = enhancedLocations[selectedPickupGroupId];
        if (selectedLocation?.addressId === firstAddressIdForEffect && isLocationFailed(selectedLocation)){
            setSelectedPickupGroupId(null);
        }
      }
    } else if (!pickupDataError && setError) {
    }
  }, [pickupDataError, setError, selectedPickupGroupId, enhancedLocations, firstAddressIdForEffect, isLocationFailed]);
  
  const selectedPickupGroup = useMemo(() => {
    return selectedPickupGroupId && enhancedLocations[selectedPickupGroupId] ? enhancedLocations[selectedPickupGroupId] : null;
  }, [selectedPickupGroupId, enhancedLocations]);

  const updateReduxPickupDetails = useCallback(() => {
    if (selectedPickupGroup) {
      const formattedPickupDetails = {
        locationId: selectedPickupGroup.id,
        locationCity: selectedPickupGroup.city || '',
        locationPostalCode: selectedPickupGroup.postalCode || '',
        merchantNames: selectedPickupGroup.merchantNames,
      };
      dispatch(setPickupDetails(formattedPickupDetails));
      dispatch(setPickupGroupDetails({
        locationId: selectedPickupGroup.id,
        city: selectedPickupGroup.city,
        postalCode: selectedPickupGroup.postalCode
      }));

      const chosenPickupAddressId = selectedPickupGroup.id;
      pickupItems.forEach(item => {
        dispatch(updateCartItemDeliveryDetails({
          itemIdentifier: item.cartItemIndex,
          cartItemIndexUsed: true,
          deliveryMethod: 'pickup',
          pickupAddressId: chosenPickupAddressId,
          itemType: item.type,
        }));
      });

      if (onComplete) onComplete(true, { locationId });
    } else {
      dispatch(setPickupDetails(null));
      if (onComplete) onComplete(false, { locationId });
    }
  }, [selectedPickupGroup, dispatch, onComplete, locationId, pickupItems]);

  useEffect(() => {
    updateReduxPickupDetails();
  }, [updateReduxPickupDetails]);

  useEffect(() => {
    if (!pickupDataLoading && Object.keys(enhancedLocations).length > 0 && !selectedPickupGroupId) {
      const locationIds = Object.keys(enhancedLocations);
      const availableLocationId = locationIds.find(id => !isLocationFailed(enhancedLocations[id]));
      if (availableLocationId) {
        setSelectedPickupGroupId(availableLocationId);
      }
    }
  }, [pickupDataLoading, enhancedLocations, selectedPickupGroupId, isLocationFailed]);

  const handleLocationSelect = useCallback((pickedLocationId) => {
    if (!isLocationFailed(enhancedLocations[pickedLocationId])) {
      setSelectedPickupGroupId(pickedLocationId);
    }
  }, [isLocationFailed, enhancedLocations]);

  if (pickupDataLoading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-4">
        <h3 className="font-medium text-lg mb-3">{title}</h3>
        <div className="flex justify-center py-8"><LoadingSpinner/></div>
      </div>
    );
  }

  if (internalError) {
    return (
      <div className="bg-white rounded-lg shadow-md p-4">
        <h3 className="font-medium text-lg mb-3">{title}</h3>
        <ErrorMessage message={internalError} />
      </div>
    );
  }
  
  if (!pickupItems || pickupItems.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-4">
        <h3 className="font-medium text-lg mb-3">{title}</h3>
        <div className="bg-gray-50 p-4 rounded-md"><p className="text-gray-500">No items selected for pickup.</p></div>
      </div>
    );
  }
  
  if (Object.keys(enhancedLocations).length === 0 && !pickupDataLoading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-4">
        <h3 className="font-medium text-lg mb-3">{title}</h3>
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
          <p className="text-yellow-700">Pickup location not available</p>
        </div>
        <ItemThumbnails items={pickupItems} />
      </div>
    );
  }

  const showGlobalPickupError = pickupDataError && Object.values(enhancedLocations).some(loc => isLocationFailed(loc));

  return (
    <div className="bg-white rounded-lg shadow-md p-4">
      <h3 className="font-medium text-lg mb-3">{title}</h3>
      {showGlobalPickupError && (
        <div className="mb-4">
          <ErrorMessage message="Some pickup locations could not be loaded. Greyed out locations are unavailable." />
        </div>
      )}
      <ItemThumbnails items={pickupItems} />
      <PickupLocationList
        enhancedLocations={enhancedLocations}
        locationDistances={locationDistances}
        selectedPickupGroupId={selectedPickupGroupId}
        isLocationFailed={isLocationFailed}
        formatPickupLocation={formatPickupLocation}
        onLocationSelect={handleLocationSelect}
      />
    </div>
  );
};

export default PickupMethodSection;